import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEmailAccounts } from "@/hooks/use-shared-queries";
import { formatDistanceToNow } from "date-fns";
import { Mail, Clock, AlertCircle, RefreshCw } from "lucide-react";
import type { EmailAccount } from "@shared/schema";
import { UnassignedEmailsDialog } from "./unassigned-emails-dialog";
import { UnlinkedEmailPulsesDialog } from "./unlinked-email-pulses-dialog";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/auth";
import { useAuth } from "@/contexts/auth-context";

export function EmailSyncStatus({ onNavigateToPulse }: {
  /** Called when navigating away to a pulse so ancestor overlays (settings modal) can close. */
  onNavigateToPulse?: () => void;
} = {}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [unassignedDialogOpen, setUnassignedDialogOpen] = useState(false);
  const [unlinkedDialogOpen, setUnlinkedDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: emailAccounts } = useEmailAccounts();

  // Find the primary email account
  const primaryAccount = emailAccounts?.find(account => account.isPrimary && account.isActive);

  const intake = useQuery<{
    progress: { stalledAt: string | null; stallUid: number | null; stallErrorCode: string | null } | null;
    pendingCount: number;
    historicalAmbiguousCount: number;
    activeLeaseCount: number;
    retryCount: number;
    failedCount: number;
    message: string;
    truncated: boolean;
    items: Array<{
      uid: number;
      reason: string;
      job: {
        stage: string;
        leaseState: 'active' | 'expired' | 'unowned';
        attempts: number;
        failed: boolean;
      } | null;
    }>;
  }>({
    queryKey: ['/api/admin/email-intake', primaryAccount?.id],
    enabled: isAdmin && !!primaryAccount,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });

  const { data: unassignedCount } = useQuery<{ count: number }>({
    queryKey: ['/api/email-accounts', primaryAccount?.id, 'unassigned-count'],
    enabled: !!primaryAccount,
  });

  // Task #48: email pulses still missing their source-email link (admin only)
  const { data: unlinkedData } = useQuery<{ count: number }>({
    queryKey: ['/api/email-pulses/unlinked'],
    enabled: isAdmin && !!primaryAccount,
    staleTime: 60000,
  });

  if (!primaryAccount) {
    return null;
  }

  // Use idleLastActivity for "last activity" timestamp (IDLE or manual fetches)
  const lastActivity = primaryAccount.idleLastActivity || primaryAccount.lastSyncTime;
  const idleStatus = primaryAccount.idleStatus || 'not_configured';
  
  const activityText = lastActivity 
    ? `Last activity: ${formatDistanceToNow(new Date(lastActivity), { addSuffix: true })}`
    : "No activity yet";

  const isRecent = lastActivity && (Date.now() - new Date(lastActivity).getTime()) < 5 * 60 * 1000; // 5 minutes
  const unassigned = unassignedCount?.count || 0;
  
  // IDLE connection status
  const isIdleConnected = idleStatus === 'connected';
  const isIdleError = idleStatus === 'error';
  const isIdleDisconnected = idleStatus === 'disconnected';
  const idleStatusText = isIdleConnected 
    ? 'IDLE: Connected' 
    : isIdleError 
    ? 'IDLE: Error'
    : isIdleDisconnected
    ? 'IDLE: Disconnected'
    : 'IDLE: Not configured';
  const idleTooltip = isIdleConnected
    ? 'IMAP IDLE is live — server pushes new mail in real time.'
    : isIdleError
    ? `IMAP IDLE error${primaryAccount.idleErrorMessage ? `: ${primaryAccount.idleErrorMessage}` : ''}. Reconnect will be attempted.`
    : isIdleDisconnected
    ? 'IMAP IDLE is disconnected. The server will attempt to reconnect automatically.'
    : 'IMAP IDLE has not been configured for this account yet.';

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await authFetch(`/api/email-accounts/${primaryAccount.id}/refresh`, {
        method: 'POST',
      });
      
      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({ queryKey: ['/api/email-accounts'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/email-messages'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/email-accounts', primaryAccount.id, 'unassigned-count'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/email-notifications'] });
      
      toast({
        title: "Success",
        description: "Emails refreshed and validated successfully",
      });
    } catch (error) {
      console.error('Refresh failed:', error);
      toast({
        title: "Error",
        description: "Failed to refresh emails. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <div className="flex items-center space-x-4 text-sm">
        {isAdmin && (intake.isError || (intake.data?.pendingCount ?? 0) > 0 || intake.data?.progress?.stalledAt) && (
          <button
            type="button"
            className={intake.data?.progress?.stalledAt
              ? "text-red-400 hover:text-red-300 text-left font-semibold"
              : "text-amber-400 hover:text-amber-300 text-left"}
            data-testid="email-intake-warning"
            onClick={(event) => {
              event.stopPropagation();
              if (intake.isError) {
                void intake.refetch();
                toast({
                  title: 'Intake status unavailable',
                  description: 'Retrying the status check. This does not recover or reprocess emails.',
                  variant: 'destructive',
                });
                return;
              }
              toast({
                title: 'Read-only intake inspection',
                description: `${intake.data?.message} ${intake.data?.items.map(
                  item => item.job
                    ? `UID ${item.uid}: ${item.job.stage}, lease ${item.job.leaseState}, attempts ${item.job.attempts}${item.job.failed ? ', failed' : ''}`
                    : `UID ${item.uid}: historical ambiguous, blocked`,
                ).join('; ')}${intake.data?.truncated ? ' (oldest 25 shown)' : ''}`,
              });
            }}
            title="Inspect pending validation. This check never sends or reprocesses email."
          >
            {intake.isError
              ? 'Intake status unavailable — retry'
              : intake.data!.progress?.stalledAt
                ? `intake stalled at UID ${intake.data!.progress.stallUid} — inspect`
              : intake.data!.failedCount > 0
                ? `${intake.data!.failedCount} intake failed — inspect`
                : intake.data!.historicalAmbiguousCount > 0
                  ? `${intake.data!.historicalAmbiguousCount} intake blocked — inspect`
                  : intake.data!.activeLeaseCount > 0
                    ? `${intake.data!.activeLeaseCount} intake active — inspect`
                    : `${intake.data!.pendingCount} intake pending — inspect`}
          </button>
        )}
        {/* Unassigned Email Count - Clickable */}
        <div 
          className="flex items-center space-x-1 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            setUnassignedDialogOpen(true);
          }}
          title="Click to view unassigned emails"
        >
          <span className="text-gray-300">Primary:</span>
          {unassigned > 0 ? (
            <span className="text-orange-400 hover:text-orange-300">
              {unassigned} unassigned
            </span>
          ) : (
            <span className="text-green-400 hover:text-green-300">
              All Emails assigned
            </span>
          )}
        </div>

        {/* Task #48: unlinked old email pulses (admin only) */}
        {isAdmin && (unlinkedData?.count ?? 0) > 0 && (
          <div
            className="flex items-center space-x-1 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              setUnlinkedDialogOpen(true);
            }}
            title="Old email pulses without a linked source email — click to review"
            data-testid="unlinked-email-pulses-count"
          >
            <span className="text-yellow-400 hover:text-yellow-300">
              {unlinkedData!.count} pulses without source email
            </span>
          </div>
        )}

        {/* IDLE Connection Status */}
        <div className="flex items-center space-x-2" title={idleTooltip}>
          <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${
              isIdleConnected ? 'bg-green-400' :
              isIdleError ? 'bg-red-400' :
              isIdleDisconnected ? 'bg-yellow-400' :
              'bg-gray-500'
            }`} />
            <span className={`text-xs ${
              isIdleConnected ? 'text-green-400' :
              isIdleError ? 'text-red-400' :
              isIdleDisconnected ? 'text-yellow-400' :
              'text-gray-500'
            }`}>
              {idleStatusText}
            </span>
          </div>
        </div>

        {/* Last Activity + Manual Refresh */}
        <div 
          className="flex items-center space-x-2 text-gray-400 hover:text-gray-300 cursor-pointer transition-colors"
          onClick={handleRefresh}
          title={
            isRefreshing
              ? 'Force-checking for new emails…'
              : isRecent
              ? `Sync OK — ${activityText.toLowerCase()}. Click to force-check now.`
              : `${activityText} (no fetch in the last 5 min). Click to force-check now.`
          }
        >
          <Mail className="w-4 h-4" />
          <span className="flex items-center space-x-1">
            {isRefreshing ? (
              <RefreshCw className="w-3 h-3 text-blue-400 animate-spin" />
            ) : isRecent ? (
              <Clock className="w-3 h-3 text-green-400" />
            ) : (
              <AlertCircle className="w-3 h-3 text-yellow-400" />
            )}
            <span className={
              isRefreshing ? "text-blue-400" : 
              isRecent ? "text-green-400" : "text-yellow-400"
            }>
              {isRefreshing ? "Force checking..." : activityText}
            </span>
          </span>
        </div>
      </div>

      {/* Unassigned Emails Dialog */}
      <UnassignedEmailsDialog
        open={unassignedDialogOpen}
        onOpenChange={setUnassignedDialogOpen}
        primaryAccount={primaryAccount}
      />

      {/* Task #48: unlinked email pulses dialog (admin only) */}
      {isAdmin && (
        <UnlinkedEmailPulsesDialog
          open={unlinkedDialogOpen}
          onOpenChange={setUnlinkedDialogOpen}
          onNavigateToPulse={onNavigateToPulse}
        />
      )}
    </>
  );
}